import { createWebSocketManager } from '../../../../utils/websocket.helper';
import { SOCKET_EVENTS } from '../../../../constants';
import { URL_CONSTANTS } from '../../../../constants/urlConstants';

/* -------------------- Project-Specific Helpers -------------------- */

const getUserData = () => {
  try {
    const userId = localStorage.getItem('user_id');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName =
      localStorage.getItem('logged_in_user') || user?.name || 'User';

    return {
      userId: userId || user?.id,
      userName,
      userData: user,
    };
  } catch (error) {
    console.error('Error reading user data', error);
    return { userId: null, userName: 'User', userData: {} };
  }
};

/* -------------------- WebSocket Manager Instance -------------------- */

let projectId = null;
let joinedProjectId = null;
let pendingProjectId = null;

// Create WebSocket manager with project-specific configuration
const wsManager = createWebSocketManager({
  url: URL_CONSTANTS.DOCUMENT.baseUrl,
  events: SOCKET_EVENTS,
  heartbeat: {
    event: SOCKET_EVENTS.HEARTBEAT,
    interval: 10000,
    getPayload: () => ({ projectId }),
  },
  maxReconnectionAttempts: 5,
  onConnect: () => {
    // Handle pending project join after connection
    if (pendingProjectId) {
      joinProjectRoom(pendingProjectId);
      pendingProjectId = null;
    }
  },
  onDisconnect: () => {
    joinedProjectId = null;
  },
});

/* -------------------- Project-Specific Functions -------------------- */

const joinProjectRoom = pid => {
  if (joinedProjectId === pid) return;

  projectId = pid;
  const socket = wsManager.getSocket();

  if (!wsManager.isConnected()) {
    pendingProjectId = pid;
    socket?.connect();
    return;
  }

  const { userId, userName } = getUserData();
  if (!userId) return;

  // Join the project room
  wsManager.emitEvent(SOCKET_EVENTS.JOIN_PROJECT, {
    projectId: pid,
    user: { id: userId, name: userName },
  });

  joinedProjectId = pid;
  pendingProjectId = null;
};

const leaveProjectRoom = pid => {
  if (!wsManager.isConnected()) return;

  const { userId } = getUserData();
  wsManager.emitEvent(SOCKET_EVENTS.LEAVE_PROJECT, {
    projectId: pid,
    userId,
  });

  if (joinedProjectId === pid) {
    joinedProjectId = null;
  }
  if (projectId === pid) {
    projectId = null;
  }
};

/* -------------------- Public API -------------------- */

const initialize = url => {
  return wsManager.initialize(url);
};

const emitEvent = (eventName, data) => {
  return wsManager.emitEvent(eventName, data);
};

const on = (eventName, callback) => {
  return wsManager.on(eventName, callback);
};

const off = (eventName, callback) => {
  return wsManager.off(eventName, callback);
};

const disconnect = () => {
  if (projectId) {
    leaveProjectRoom(projectId);
  }
  wsManager.disconnect();
  projectId = null;
  joinedProjectId = null;
  pendingProjectId = null;
};

const reconnect = () => {
  return wsManager.reconnect();
};

const isConnected = () => {
  return wsManager.isConnected();
};

const getSocketId = () => {
  return wsManager.getSocketId();
};

const sendHeartbeat = () => {
  return wsManager.sendHeartbeat();
};

const stopHeartbeat = () => {
  return wsManager.stopHeartbeat();
};

/* -------------------- Export Singleton -------------------- */

export default {
  initialize,
  joinProjectRoom,
  leaveProjectRoom,
  emitEvent,
  on,
  off,
  disconnect,
  reconnect,
  isConnected,
  getSocketId,
  sendHeartbeat,
  stopHeartbeat,
};
